import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faCalendar, 
  faShoppingBag, 
  faEye, 
  faLock,
  faSignOutAlt,
  faArrowLeft,
  faEyeSlash,
  faEdit,
  faCog,
  faBox
} from '@fortawesome/free-solid-svg-icons';
import AuthService from '../utils/authService';
import { useScrollToTop } from '../hooks/useScrollToTop';

interface Order {
  id: string;
  date: string;
  total: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  items: {
    name: string;
    quantity: number;
    price: number;
  }[];
}

const ProfilePage: React.FC = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const [user, setUser] = useState(AuthService.getUser());
  const [orders, setOrders] = useState<Order[]>([]);
  const [activeTab, setActiveTab] = useState<'profile' | 'orders'>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const mockOrders: Order[] = [
      {
        id: 'ORD-001',
        date: '2024-10-01',
        total: 89.99,
        status: 'delivered',
        items: [
          { name: 'Premium Shilajit Resin - 30g', quantity: 1, price: 89.99 }
        ]
      },
      {
        id: 'ORD-002',
        date: '2024-09-15',
        total: 159.98,
        status: 'shipped',
        items: [
          { name: 'Premium Shilajit Resin - 30g', quantity: 2, price: 79.99 }
        ]
      },
      {
        id: 'ORD-003',
        date: '2024-08-20',
        total: 49.99,
        status: 'delivered',
        items: [
          { name: 'Shilajit Capsules - 60 count', quantity: 1, price: 49.99 }
        ]
      }
    ];

    setOrders(mockOrders);
  }, [user, navigate]);

  const handleLogout = () => {
    AuthService.logout();
    navigate('/');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    setIsLoading(true);
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-100';
      case 'shipped': return 'text-blue-600 bg-blue-100';
      case 'processing': return 'text-yellow-600 bg-yellow-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-20">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/"
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-[#2b603e] transition-all duration-200"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Account</h1>
                <p className="text-gray-500">Manage your profile and orders</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* User Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border p-8 mb-8">
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 bg-gradient-to-br from-[#2b603e] to-[#1a3d2a] rounded-2xl flex items-center justify-center shadow-lg">
              <FontAwesomeIcon icon={faUser} className="text-white text-2xl" />
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 text-left">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-gray-600 text-lg mb-1 text-left">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl mb-8 w-fit">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'profile'
                ? 'bg-white text-[#2b603e] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FontAwesomeIcon icon={faUser} className="mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'orders'
                ? 'bg-white text-[#2b603e] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FontAwesomeIcon icon={faBox} className="mr-2" />
            Orders ({orders.length})
          </button>
        </div>

        {/* Profile Information Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Account Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-left">Account Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faUser} className="text-[#2b603e] mr-2" />
                      <span className="text-sm font-medium text-gray-600">First Name</span>
                    </div>
                    <p className="text-lg font-medium text-gray-900 text-left">{user.firstName}</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faUser} className="text-[#2b603e] mr-2" />
                      <span className="text-sm font-medium text-gray-600">Last Name</span>
                    </div>
                    <p className="text-lg font-medium text-gray-900 text-left">{user.lastName}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faEnvelope} className="text-[#2b603e] mr-2" />
                      <span className="text-sm font-medium text-gray-600">Email Address</span>
                    </div>
                    <p className="text-lg font-medium text-gray-900 text-left">{user.email}</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faCalendar} className="text-[#2b603e] mr-2" />
                      <span className="text-sm font-medium text-gray-600">Member Since</span>
                    </div>
                    <p className="text-lg font-medium text-gray-900 text-left">October 2024</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Password Change Section */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-left">Security Settings</h3>
              
              {/* Success/Error Messages */}
              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  {passwordSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Current Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      <FontAwesomeIcon icon={faLock} className="text-[#2b603e] mr-2" />
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2b603e]/20 focus:border-[#2b603e] transition-all duration-200"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      <FontAwesomeIcon icon={faLock} className="text-[#2b603e] mr-2" />
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2b603e]/20 focus:border-[#2b603e] transition-all duration-200"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      <FontAwesomeIcon icon={faLock} className="text-[#2b603e] mr-2" />
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2b603e]/20 focus:border-[#2b603e] transition-all duration-200"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-start text-left">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`bg-[#2b603e] hover:bg-[#1a3d2a] text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'
                    }`}
                  >
                    {isLoading ? 'Updating Password...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faShoppingBag} className="text-gray-400 text-2xl" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Orders Yet</h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">
                  You haven't placed any orders yet. Start exploring our premium products!
                </p>
                <Link
                  to="/products"
                  className="bg-[#2b603e] hover:bg-[#1a3d2a] text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg inline-flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={faShoppingBag} />
                  <span>Start Shopping</span>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-[#2b603e]/10 rounded-xl flex items-center justify-center">
                          <FontAwesomeIcon icon={faBox} className="text-[#2b603e] text-lg" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Order #{order.id}</h3>
                          <p className="text-gray-500">{formatDate(order.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`px-4 py-2 rounded-xl text-sm font-medium capitalize ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">${order.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900">Order Items</h4>
                        <button className="text-[#2b603e] hover:text-[#1a3d2a] text-sm font-medium transition-colors duration-200 flex items-center space-x-2">
                          <FontAwesomeIcon icon={faEye} />
                          <span>View Details</span>
                        </button>
                      </div>
                      <div className="space-y-3">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-[#2b603e]">${item.price.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
